
import { LibraryVisibilities } from '@/modules/library/constants'
import { LibraryVisibility } from '@/modules/library/types'
import { LibraryCategorySelectItem } from '@/store/modules/teamlibrary/types'
import IconChevronDown from '@svg/IconChevronDown.svg'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { VSelect } from 'vuetify/lib'
import { Action, Getter, State } from 'vuex-class'

/**
 * The LibraryFilters component provides filtering functionality for library objects with the following filters:
 * - 1. Tags: Multi-select filter to filter objects by tags
 * - 2. Category: Single-select filter to filter objects by category
 * - 3. Visibility: Single-select filter to filter objects by visibility (JigSpaceStaff only)
 * - 4. Date: Single-select filter to filter objects by date updated (Today, Yesterday, Last 30 days, Last 90 days, Last year)
 *
 * Features:
 * - Displays total object count in summary section
 * - Shows "Clear filters" button when any filter is active
 * - Emits 'filter-updated' event when a filter changes with key, operator, and value
 * - Emits 'filter-cleared' event when all filters are cleared
 *
 * Operator Reference:
 * - `eq` - Equals
 * - `gt` - Greater than
 * - `gte` - Greater than or equal
 * - `lt` - Less than
 * - `lte` - Less than or equal
 * - `like` - Substring match (case-insensitive)
 * - `any` - Matches any of the comma-separated values
 * - `contains` - Array contains ALL specified values
 * - `intersects` - Array contains ANY of the specified values
 *
 * Notes:
 * - Tags filter uses 'intersects' operator with comma-separated values
 * - Category filter uses 'contains' operator with category ID
 * - Visibility filter uses 'eq' operator when a specific visibility is selected;
 *   when cleared, defaults to 'any' (all visibilities) for JigSpaceStaff or 'eq' (tenant only) for regular users
 * - Date filter uses 'gte' operator with ISO timestamp format including microseconds
 * - All select dropdowns auto-blur after selection for better UX
 */
@Component({
  components: {
    IconChevronDown,
  },
})
export default class LibraryFiltersVue extends Vue {
  @Prop({ type: Number, default: 0 })
  public totalQty!: number
  @Prop({ type: Array, default: [] })
  public libraryTags!: string[]
  @Prop({ type: Array, default: [] })
  public libraryCategoryItems!: LibraryCategorySelectItem[]
  @Prop({ type: Boolean, default: false })
  public isJigStaffView!: boolean

  private libraryVisibilities: LibraryVisibility[] = LibraryVisibilities
  private selectedTags: string[] = []
  private selectedCategory: number = -1
  private selectedVisibility: string = ''
  private selectedDate: Record<string, any> = {}
  private libraryObjectDate: {
    text: string
    data: Record<string, any>
  }[] = [
    { text: 'Today', data: { value: 0, unit: 'day' } },
    { text: 'Yesterday', data: { value: -1, unit: 'day' } },
    { text: 'Last 30 days', data: { value: -30, unit: 'day' } },
    { text: 'Last 90 days', data: { value: -90, unit: 'day' } },
    { text: 'Last year', data: { value: -1, unit: 'year' } },
  ]

  public $refs!: Vue['$refs'] & {
    tagSelect: any
    categorySelect: any
    visibilitySelect: any
    datetimeSelect: any
  }

  private get isFilterActive(): boolean {
    return (
      this.selectedTags.length > 0 ||
      this.selectedCategory >= 0 ||
      this.selectedVisibility !== '' ||
      Object.keys(this.selectedDate).length > 0
    )
  }

  private get defaultVisibilityFilter(): { op: string; value: string } {
    return this.isJigStaffView ? { op: 'any', value: 'public,tenant,private' } : { op: 'eq', value: 'tenant' }
  }

  @Watch('isJigStaffView', { immediate: true })
  private onIsJigStaffViewChanged() {
    this.emitDefaultVisibility()
  }

  private emitDefaultVisibility() {
    const { op, value } = this.defaultVisibilityFilter
    this.$emit('filter-updated', {
      key: 'visibility',
      op,
      value,
    })
  }

  private updateObjectTags(val: any) {
    if (val == null) {
      this.selectedTags = []
    }

    this.$emit('filter-updated', {
      key: 'tags',
      op: 'intersects',
      value: this.selectedTags.join(','),
    })

    this.$refs.tagSelect.blur()
  }

  private updateObjectCategory(val: any) {
    if (val == null) {
      this.selectedCategory = -1
    }

    this.$emit('filter-updated', {
      key: 'categories',
      op: 'contains',
      value: this.selectedCategory === -1 ? null : this.selectedCategory,
    })

    this.$refs.categorySelect.blur()
  }

  private updateObjectVisibility(val: any) {
    if (val == null) {
      this.selectedVisibility = ''
    }

    // When clearing visibility filter, use default based on user type
    if (this.selectedVisibility === '') {
      this.emitDefaultVisibility()
    } else {
      this.$emit('filter-updated', {
        key: 'visibility',
        op: 'eq',
        value: this.selectedVisibility,
      })
    }

    this.$refs.visibilitySelect.blur()
  }

  private updateObjectDate(val: any) {
    if (val == null) {
      this.selectedDate = {}

      this.$emit('filter-updated', {
        key: 'date_updated',
        op: 'gte',
        value: '',
      })

      this.$refs.datetimeSelect.blur()

      return
    }

    const { value, unit } = this.selectedDate
    // base on "now"
    const date = new Date()
    // Reset to start of day
    // This ensures:
    // Today → from 00:00:00 today
    // Yesterday → from 00:00:00 yesterday
    // Last 30 days → from 00:00:00 30 days ago
    date.setHours(0, 0, 0, 0)

    switch (unit) {
      case 'day':
        date.setUTCDate(date.getUTCDate() + value)
        break
      case 'year':
        date.setUTCFullYear(date.getUTCFullYear() + value)
        break
      default:
        break
    }

    this.$emit('filter-updated', {
      key: 'date_updated',
      op: 'gte',
      value: this.toIsoWithMicros(date),
    })

    this.$refs.datetimeSelect.blur()
  }

  /**
   * Convert a Date to `YYYY-MM-DDTHH:mm:ss.ffffffZ`
   * (JS only gives milliseconds, so we zero-pad extra 3 digits)
   */
  private toIsoWithMicros(date: Date): string {
    const iso = date.toISOString() // e.g. 2019-05-22T05:38:55.123Z

    const [datePart, timePart] = iso.split('T')
    const [hms, msAndZ] = timePart.split('.')
    const ms = msAndZ.slice(0, 3) // "123"
    const z = msAndZ.slice(3) // "Z"

    const micros = ms + '000' // "123000" -> 6 digits

    return `${datePart}T${hms}.${micros}${z}` // 2019-05-22T05:38:55.123000Z
  }

  private clearFilters() {
    this.selectedTags = []
    this.selectedCategory = -1
    this.selectedVisibility = ''
    this.selectedDate = {}

    this.$emit('filter-cleared')
    // Emit default visibility after clearing all filters
    this.emitDefaultVisibility()
  }
}
